import {
   call,
   put,
   take,
   takeEvery,
   takeLatest,
   cancelled,
   SagaReturnType,
} from 'redux-saga/effects';
import {get} from 'lodash';

import {action, Action} from 'redux/helpers';
import {DiplomatObjectEndpoints} from 'api/DiplomatObjectEndpoints';
import {DialogResult, DialogTypes} from 'common/names';
import {pushErrorToast} from 'features/notifications/redux/actions';
import {asyncSagaConstructor} from 'redux/common';
import {packId, unpackId} from 'common/diplomatIdUtil';
import {updatePartnerSaga} from 'features/layout/CommonPage/redux/sagas/updatePartner';
import {IDiplomatObject} from 'types/DiplomatObject';
import {DiplomatObjectType, DiplomatObjectTypeUnion} from 'types/DiplomatObjectType';
import ApiService from 'api/ApiService';
import {pageTypes} from 'features/layout/CommonPage/redux';
import {layoutActions, layoutTypes} from 'features/layout/redux';
import i18n from 'i18n';
import history from 'redux/history';

function* openConfirmDialog() {
   yield put(
      layoutActions.openSubDialog({
         id: DialogTypes.ConfirmationDialog.id,
         title: i18n.t('Subkey'),
         content: i18n.t(`Do you want to add a subkey?`),
      })
   );
   const result: Action = yield take(layoutTypes.CLOSE_SUB_DIALOG);
   return result.payload.result === DialogResult.OK;
}

function* validateSendToServer(act: Action) {
   try {
      const data = act.payload?.values;
      const endpoint = get(DiplomatObjectEndpoints, data.objectType);
      // TODO get schema and castToSend
      const api = data.id ? `${endpoint}/${data.id}` : endpoint;
      const getMethod = data.id ? ApiService.putSecureJson : ApiService.postSecureJson;

      const postSaga = (yield call(asyncSagaConstructor, api, pageTypes.SEND_DATA, {
         getMethod,
         data,
      })) as SagaReturnType<typeof asyncSagaConstructor>;
      const resp = (yield call(postSaga)) as SagaReturnType<typeof postSaga>;

      if (act.callbacks) {
         act.callbacks.setSubmitting(false);
      }

      return resp;
   } catch (e) {
      if (act.callbacks) {
         act.callbacks.setErrors(e);
         act.callbacks.setSubmitting(false);
      }
      throw e;
   }
}

function* goToCreated(resp: IDiplomatObject) {
   if (resp.id) {
      yield call(history.push, packId(resp.objectType, resp.id));
   }
}

/**
 * @deprecated should be removed
 * @param act
 */
function* sendDataSaga(act: Action) {
   try {
      // console.debug(act);
      const response = (yield call(validateSendToServer, act)) as SagaReturnType<
         typeof validateSendToServer
      >;

      yield call(goToCreated, response as IDiplomatObject);

      if (act.payload._dialogType === DialogTypes.CreateOpenPGPKeyPair.id) {
         const dialogRes = (yield call(openConfirmDialog)) as SagaReturnType<
            typeof openConfirmDialog
         >;
         if (dialogRes) {
            yield put(action(layoutTypes.OPEN_DIALOG, DialogTypes.AddOpenPGPSubkeys.id));
         }
      }
   } catch (e) {
      console.error(e);
   }
   // yield call(act.payload.callbacks.setErrors, {name: 'test error from saga'});
}

function* getDataSaga(act: {
   type: string;
   payload: {id: string; objectType: DiplomatObjectTypeUnion};
}) {
   try {
      const {id: _id, objectType} = act.payload;

      if (!_id) {
         yield put(action(pageTypes.GET_DATA.SUCCESS, {}));
         return;
      }

      if (objectType === DiplomatObjectType.enum.VFS) {
         yield put(
            action(pageTypes.GET_DATA.SUCCESS, {
               id: _id,
               objectType: objectType,
            })
         );
         return;
      }
      // try to unpack id
      const {id} = unpackId(_id);

      const endpoint = get(DiplomatObjectEndpoints, objectType);
      if (!endpoint) {
         yield put(pushErrorToast(`${i18n.t('Unsupported object type')} ${objectType}`));
      }

      let getSaga = (yield call(
         asyncSagaConstructor,
         `${endpoint}/${id}`,
         pageTypes.GET_DATA
      )) as SagaReturnType<typeof asyncSagaConstructor>;
      yield call(getSaga);
   } catch (e) {
      console.error(e);
   } finally {
      if ((yield cancelled()) as boolean) {
         // console.debug('cancelled');
      }
   }
}

export function* commonPageSagas() {
   yield takeEvery(pageTypes.SEND_DATA.SAGA, sendDataSaga);
   yield takeLatest(pageTypes.GET_DATA.SAGA, getDataSaga);
   yield takeLatest(pageTypes.UPDATE_PARTNER.SAGA, updatePartnerSaga);
}
